<script setup>
import {useConfigurateurStore} from "../../stores/configurateur";
import {ref, computed, watch} from 'vue';
import Logger from "../../utils/logger";

const logger = new Logger('configurateur')
const store = useConfigurateurStore();

// Debug pour comprendre l'origine du problème avec possibilites undefined
watch(() => store.stage, (newStage, oldStage) => {
    logger.log('🐛 StepTourReserv - Stage changed:', {
        oldStage: oldStage ? {
            keys: Object.keys(oldStage),
            stage_id: oldStage.stage_id,
            product_id: oldStage.product_id,
            possibilites: oldStage.possibilites,
            has_possibilites: !!oldStage.possibilites,
            possibilites_length: oldStage.possibilites?.length,
            prices: oldStage.prices,
            has_prices: !!oldStage.prices,
            type: oldStage.possibilites?.length ? 'possibilites' : 'normal'
        } : 'null/undefined',
        newStage: newStage ? {
            keys: Object.keys(newStage),
            stage_id: newStage.stage_id,
            product_id: newStage.product_id,
            possibilites: newStage.possibilites,
            has_possibilites: !!newStage.possibilites,
            possibilites_length: newStage.possibilites?.length,
            prices: newStage.prices,
            has_prices: !!newStage.prices,
            type: newStage.possibilites?.length ? 'possibilites' : 'normal'
        } : 'null/undefined',
        is_empty_object: newStage && Object.keys(newStage).length === 0
    });
}, {deep: true, immediate: true});
const isCollapsed = ref(false);
const tempNbToursBapteme = ref(1); // Variable temporaire pour le contrôleur
const showModal = ref(false);
const modalType = ref('');
watch(() => [store.stageBapteme.stage_id, store.stageBapteme.nb_tour_min, store.stageBapteme.nb_tour_max], () => {
    store.clampBaptemeTours();
    const minimum = Number(store.stageBapteme.nb_tour_min) || 1;
    const maximum = Number(store.stageBapteme.nb_tour_max) || minimum;
    tempNbToursBapteme.value = Math.min(maximum, Math.max(minimum, tempNbToursBapteme.value));
}, { immediate: true });

const showToursInfo = () => {
    modalType.value = 'tours';
    showModal.value = true;
}

const showBaptemeInfo = () => {
    modalType.value = 'bapteme';
    showModal.value = true;
}

const closeModal = () => {
    showModal.value = false;
    modalType.value = '';
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
}

// Fonctions pour gérer le baptême temporaire (avant ajout)
const handleClickDecrementTempBapteme = () => {
    if (tempNbToursBapteme.value <= 1) {
        return;
    }
    tempNbToursBapteme.value--;
}

const handleClickIncrementTempBapteme = () => {
    if (!store.stageBapteme.nb_tour_max || tempNbToursBapteme.value >= store.stageBapteme.nb_tour_max) {
        return;
    }
    tempNbToursBapteme.value++;
}

// Fonction pour ajouter/mettre à jour le baptême
const handleAddOrUpdateBapteme = () => {
    if (isBaptemeAdded.value) {
        // Mode mise à jour : utilise la valeur du contrôleur store
        tempNbToursBapteme.value = store.nb_tours_bapteme;
    } else {
        // Mode ajout : set la valeur du store depuis la variable temporaire
        store.nb_tours_bapteme = tempNbToursBapteme.value;
    }

    // Logique existante du store pour ajouter le baptême
    if (!store.stageBaptemeInStageIds && store.stageBapteme) {
        store.stage_ids.push(store.stageBapteme.form);
    }
    if (store.stageBaptemeInStageIds) {
        store.stageBaptemeInStageIds.nb_tour = store.nb_tours_bapteme;
    }
}

// Fonction pour supprimer le baptême
const handleRemoveBapteme = () => {
    store.nb_tours_bapteme = 0;
    tempNbToursBapteme.value = 1; // Remise à zéro de la variable temporaire
    // Supprimer de stage_ids si présent
    if (store.stageBaptemeInStageIds) {
        store.stage_ids = store.stage_ids.filter(v => v.id != store.stageBaptemeInStageIds.id);
    }
}

// Computed pour savoir si le baptême est ajouté
const isBaptemeAdded = computed(() => {
    return store.nb_tours_bapteme > 0;
})

const handleClickDecrementTour = () => {
    // Permettre de décrémenter jusqu'à 0
    if (store.nb_tours <= 0) {
        return
    }

    const nbTourMin = parseInt(store.stage.nb_tour_min) || 2;

    // Si on est au-dessus du minimum, décrémenter normalement
    if (store.nb_tours > nbTourMin) {
        store.decrementNbTours()
    }
    // Si on est exactement au minimum, aller directement à 0
    else if (store.nb_tours == nbTourMin) {
        store.setNbTours(0)
    }

}

const handleClickIncrementTour = () => {
    const nbTourMax = parseInt(store.stage.nb_tour_max) || 50;
    const nbTourMin = parseInt(store.stage.nb_tour_min) || 2;

    if (store.nb_tours >= nbTourMax) {
        return
    }

    // Si on est à 0, aller directement au minimum
    if (store.nb_tours == 0) {
        store.setNbTours(nbTourMin)
    }
    // Sinon, incrémenter normalement
    else {
        store.incrementNbTours()
    }

}

const handleClickDecrementTourBapteme = () => {
    // En mode baptême pur : ne pas descendre en dessous du minimum
    if (store.experience === 'bapteme') {
        if (store.nb_tours_bapteme <= store.stageBapteme.nb_tour_min) {
            return;
        }
        store.decrementNbToursBapteme();
        return;
    }

    // En mode pilotage + baptême : permettre de décrémenter jusqu'à 0
    if (store.nb_tours_bapteme <= 0) {
        return;
    }

    // Si on est au-dessus du minimum, décrémenter normalement
    if (store.nb_tours_bapteme > store.stageBapteme.nb_tour_min) {
        store.decrementNbToursBapteme();
    }
    // Si on est exactement au minimum, aller directement à 0
    else if (store.nb_tours_bapteme == store.stageBapteme.nb_tour_min) {
        store.nb_tours_bapteme = 0;
    }
}

const handleClickIncrementTourBapteme = () => {
    if (store.nb_tours_bapteme >= store.stageBapteme.nb_tour_max) {
        return
    }

    // Si on est à 0, aller directement au minimum
    if (store.nb_tours_bapteme == 0) {
        store.nb_tours_bapteme = store.stageBapteme.nb_tour_min;
    }
    // Sinon, incrémenter normalement
    else {
        store.incrementNbToursBapteme()
    }
}

</script>

<template>
    <section id="step-tour">
        <div class="card-step-group shadow-sm">
            <div class="step-title d-flex justify-content-between align-items-center">
                <div class="circle"
                     :class="{'active': store.getStepNumber('tour') == store.step, 'completed': store.isCompleted(store.getStepNumber('tour'))}">
                    {{ store.getStepNumber('tour') }}
                </div>
                <span class="h5 large fw-bolder mb-2">{{ $t('Nombre de tour') }}</span>
                <button class="btn btn-sm" type="button" @click="toggleCollapse">
                    <i class="bi" :class="isCollapsed ? 'bi-plus-circle' : 'bi-dash-circle'"></i>
                </button>
            </div>
            <div id="collapse-tours"
                 class="mb-4 pb-1 card-step d-sm-block card card-step"
                 :class="{ 'show': !isCollapsed, 'collapse': isCollapsed }">
                <div class="card-body">
                    <div class="row">
                        <div id="list-circuits" class="col-md-12">
                            <!-- Mode Baptême : afficher contrôleur ou possibilités du stage baptême uniquement -->
                            <template v-if="store.experience === 'bapteme'">
                                <!-- Contrôleur principal pour baptême -->
                                <template
                                    v-if="(!store.stageBapteme.possibilites || !store.stageBapteme.possibilites.length)">
                                    <div class="main-controller bg-primary rounded p-3 mb-4">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <button @click.prevent="handleClickDecrementTourBapteme"
                                                    class="btn btn-circle btn-white"
                                                    type="button"
                                                    :disabled="store.experience === 'bapteme' ? store.nb_tours_bapteme <= store.stageBapteme.nb_tour_min : store.nb_tours_bapteme <= 0">
                                                <i class="bi bi-dash fs-3"></i>
                                            </button>

                                            <div class="tour-number text-white text-center">
                                                <span class="fs-1 fw-bold">{{ store.nb_tours_bapteme }}</span>
                                            </div>

                                            <button @click.prevent="handleClickIncrementTourBapteme"
                                                    class="btn btn-circle btn-white"
                                                    type="button"
                                                    :disabled="store.nb_tours_bapteme >= store.stageBapteme.nb_tour_max">
                                                <i class="bi bi-plus fs-3"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <!-- Lien d'aide pour le nombre de tours -->
                                    <div class="tour-help-link text-center mb-4 col-6">
                                        <button @click.prevent="showBaptemeInfo"
                                                class="btn btn-link p-0 text-primary">
                                            {{ $t('Comment choisir le bon nombre de tours ?') }}
                                        </button>
                                    </div>
                                </template>

                                <!-- Possibilités pour baptême -->
                                <div class="row g-3"
                                     v-if="store.stageBapteme.possibilites && store.stageBapteme.possibilites.length">
                                    <div @click="store.stageBapteme.possibilite = possibilite"
                                         v-for="(possibilite, index) in store.stageBapteme.possibilites" :key="index"
                                         class="col-6">
                                        <div class="suggestion-card bg-light rounded p-3 text-center cursor-pointer"
                                             @click="store.setPossibilite(possibilite)">
                                            <input v-model="store.stageBapteme.possibilite" :value="possibilite"
                                                   class="form-check-input"
                                                   type="radio" name="tour-bapteme" :id="'tour-bapteme-' + index">

                                            <label class="form-check-label" :for="'tour-bapteme-' + index">
                                                <p class="text-black m-0 pb-1">{{ possibilite.designation }}</p>

                                                <div class="possibility-price">
                                    <span v-if="store.getPossibilitePrice(possibilite).promo"
                                          class="badge bg-danger text-white text-center fw-bolder">{{
                                            store.getPossibilitePrice(possibilite).promo
                                        }}</span>
                                                    <span
                                                        v-if="store.getPossibilitePrice(possibilite).promo && store.getPossibilitePrice(possibilite).old_montant_ttc && store.getPossibilitePrice(possibilite).old_montant_ttc !== store.getPossibilitePrice(possibilite).montant_ttc"
                                                        class="old-price fw-normal text-decoration-line-through small fs-xxl-6">{{
                                                            store.getPossibilitePrice(possibilite).old_montant_ttc
                                                        }}€</span>
                                                    <span
                                                        class="price fw-bolder text-info small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">{{
                                                            store.getPossibilitePrice(possibilite).montant_ttc
                                                        }}€</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- Mode Pilotage : afficher contrôleur ou possibilités du stage principal -->
                            <template v-else>
                                <!-- Contrôleur principal grand format -->
                                <template
                                    v-if="(!store.stage.possibilites || !store.stage.possibilites.length) && !store.stage.is_bapteme">
                                    <div class="main-controller bg-primary rounded p-3 mb-4">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <button @click.prevent="handleClickDecrementTour"
                                                    class="btn btn-circle btn-white"
                                                    type="button"
                                                    :disabled="store.nb_tours == store.stage.nb_tour_min">
                                                <i class="bi bi-dash fs-3"></i>
                                            </button>

                                            <div class="tour-number text-white text-center">
                                                <span class="fs-1 fw-bold">{{ store.nb_tours }}</span>
                                            </div>

                                            <button @click.prevent="handleClickIncrementTour"
                                                    class="btn btn-circle btn-white"
                                                    type="button"
                                                    :disabled="store.nb_tours == store.stage.nb_tour_max">
                                                <i class="bi bi-plus fs-3"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Suggestions de formules -->
                                    <div class="formules-suggestions mb-4" v-if="store.rangeTours">
                                        <div class="row g-3">
                                            <div v-for="(tour, index) in store.rangeTours.slice(0, 3)" :key="index"
                                                 class="col-6">
                                                <div @click.prevent="store.setNbTours(tour.nb_tours)"
                                                     class="suggestion-card bg-light rounded p-3 text-center cursor-pointer"
                                                     :class="{ 'selected': store.nb_tours == tour.nb_tours }">
                                                    <div class="suggestion-label fw-bold small text-muted mb-1">
                                                        {{ tour.label }} :
                                                    </div>
                                                    <div class="suggestion-number fw-bold fs-4">{{
                                                            tour.nb_tours
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Lien d'aide pour le nombre de tours -->
                                            <div class="tour-help-link text-center mb-4 col-6">
                                                <button @click.prevent="showToursInfo"
                                                        class="btn btn-link p-0 text-primary">
                                                    {{ $t('Comment choisir le bon nombre de tours ?') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <!-- Possibilités pour pilotage -->
                                <div class="row g-3" v-if="store.stage.possibilites && store.stage.possibilites.length">
                                    <div @click="store.setPossibilite(possibilite)"
                                         v-for="(possibilite, index) in store.stage.possibilites" :key="index"
                                         class="col-6">
                                        <div class="suggestion-card bg-light rounded p-3 text-center cursor-pointer">
                                            <input v-model="store.possibilite" :value="possibilite"
                                                   class="form-check-input"
                                                   type="radio" name="tour" :id="'tour-' + index">

                                            <label class="form-check-label" :for="'tour-' + index">
                                                <p class="text-black m-0 pb-1">{{ possibilite.designation }}</p>

                                                <div class="possibility-price">
                                    <span v-if="store.getPossibilitePrice(possibilite).promo"
                                          class="badge bg-danger text-white text-center fw-bolder">{{
                                            store.getPossibilitePrice(possibilite).promo
                                        }}</span>
                                                    <span
                                                        v-if="store.getPossibilitePrice(possibilite).promo && store.getPossibilitePrice(possibilite).old_montant_ttc && store.getPossibilitePrice(possibilite).old_montant_ttc !== store.getPossibilitePrice(possibilite).montant_ttc"
                                                        class="old-price fw-normal text-decoration-line-through small fs-xxl-6">{{
                                                            store.getPossibilitePrice(possibilite).old_montant_ttc
                                                        }}€</span>
                                                    <span
                                                        class="price fw-bolder text-info small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">{{
                                                            store.getPossibilitePrice(possibilite).montant_ttc
                                                        }}€</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Modal d'information -->
            <div v-if="showModal" class="step-modal-overlay" @click="closeModal">
                <div class="step-modal-content" @click.stop>
                    <div class="step-modal-header">
                        <h3 class="step-modal-title">
                            <span v-if="modalType === 'tours'">{{ $t('Comment faire le bon choix') }}</span>
                            <span v-if="modalType === 'bapteme'">{{ $t('Fiche page baptême passager') }}</span>
                        </h3>
                        <button type="button" class="btn-close" @click="closeModal">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div class="step-modal-body">
                        <div v-if="modalType === 'tours'">
                            <h5 class="text-primary mb-3">{{ $t('Pourquoi choisir plus de tours ?') }}</h5>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('🚗 Prise en main progressive') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('Les premiers tours vous permettent de vous familiariser avec le véhicule, ses réactions et la géométrie du circuit. C\'est le moment d\'apprivoiser la puissance et de prendre vos repères.')
                                    }}
                                </p>
                            </div>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('⚡ Montée en performance') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('À partir du 4ème tour, vous commencez à être en confiance. C\'est là que vous pouvez vraiment exploiter le potentiel de la voiture et ressentir des sensations plus intenses.')
                                    }}
                                </p>
                            </div>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('🏁 Expérience optimale') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('Avec 6 tours ou plus, vous entrez dans une véritable session de pilotage. Vous pouvez travailler votre technique, vos trajectoires et profiter pleinement de l\'adrénaline.')
                                    }}
                                </p>
                            </div>

                            <div class="alert alert-info">
                                <small>
                                    <strong>{{ $t('💡 Conseil d\'expert :') }}</strong>
                                    {{
                                        $t('Plus vous roulez, plus vous progressez et plus l\'expérience devient mémorable. C\'est un investissement dans le plaisir !')
                                    }}
                                </small>
                            </div>
                        </div>

                        <div v-if="modalType === 'bapteme'">
                            <h5 class="text-primary mb-3">{{ $t('Le baptême passager, qu\'est-ce que c\'est ?') }}</h5>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('🎯 Une expérience unique') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('Le baptême passager vous permet de découvrir les sensations de la vitesse sans conduire. Vous êtes aux côtés d\'un pilote professionnel qui maîtrise parfaitement le véhicule.')
                                    }}
                                </p>
                            </div>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('👥 Parfait pour accompagner') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('Idéal pour faire participer votre conjoint(e), un ami ou un membre de votre famille à l\'aventure. Une façon de partager l\'expérience ensemble !')
                                    }}
                                </p>
                            </div>

                            <div class="mb-4">
                                <h6 class="fw-bold mb-2">{{ $t('🚀 Sensations garanties') }}</h6>
                                <p class="small mb-3">
                                    {{
                                        $t('Accélérations, freinages appuyés, prises de virage à haute vitesse... Vous vivez toute l\'intensité du pilotage sportif en toute sécurité.')
                                    }}
                                </p>
                            </div>

                            <div class="alert alert-success">
                                <small>
                                    <strong>{{ $t('🎁 Bon à savoir :') }}</strong>
                                    {{
                                        $t('Le baptême passager est souvent un excellent moyen de se rassurer avant de prendre le volant soi-même !')
                                    }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    </section>
</template>

<style scoped>
/* Contrôleur principal */
.main-controller {
    background: #4285f4 !important; /* Bleu maquette */
}

.btn-circle {
    width: 50px;
    height: 50px;
    border-radius: 50% !important;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
}

.btn-white {
    background: white;
    color: #333;
}

.btn-white:hover {
    background: #f8f9fa;
    color: #333;
}

.btn-white:disabled {
    background: #e9ecef;
    color: #6c757d;
    opacity: 0.6;
}

.tour-number {
    flex: 1;
}

/* Suggestions de formules */
.suggestion-card {
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    min-height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.suggestion-card:hover {
    background: #e9ecef !important;
    transform: translateY(-2px);
}

.suggestion-card.selected {
    border-color: #4285f4;
    background: #e3f2fd !important;
}

.suggestion-label {
    font-size: 0.8rem;
}

.suggestion-number {
    color: #333;
}

/* Keep formula discounts in the price flow at every breakpoint. */
.possibility-price {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.25rem 0.5rem;
}

.possibility-price > span {
    white-space: nowrap;
}

.possibility-price > .badge {
    position: static;
    inset: auto;
    transform: none;
    scale: 1;
    z-index: auto;
    flex-shrink: 0;
}

/* Lien d'aide */
.tour-help-link {
    display: flex;
    align-items: center;
    justify-content: center;
}

hr {
    border-top: 2px solid;
}

/* Section Baptême */
.bapteme-section {
}

.bapteme-controller {

}

.bapteme-controller .btn.btn-circle {
    width: 40px;
    height: 40px;
    border: 2px solid #6c757d;
    background: white;
}

.bapteme-controller .btn.btn-circle:hover {
    background: #6c757d;
    color: white;
}

.bapteme-controller .btn.btn-circle:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.bapteme-price-display {
    text-align: center;
    padding: 1.3rem;
}

/* Utilitaires */
.cursor-pointer {
    cursor: pointer;
}

/* Responsive */
@media (max-width: 768px) {
    .main-controller .d-flex {
        gap: 10px;
    }

    .suggestion-card {
        min-height: 70px;
    }

    .bapteme-controller .d-flex {
        gap: 10px;
    }

    .bapteme-price-display {
        min-width: 60px;
    }
}
</style>
